import { Outlet, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '../store/auth';

// Premium Logo - Black/White Gradient
const Logo = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="40" height="40" rx="12" fill="url(#logo-grad-auth)" />
    <path 
      d="M12 14H18V16H14V24H18V26H12V14ZM22 14H28V26H22V24H26V16H22V14ZM16 18H24V22H16V18Z" 
      fill="white"
    />
    <defs>
      <linearGradient id="logo-grad-auth" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
        <stop stopColor="#2d2d2d" />
        <stop offset="1" stopColor="#0a0a0a" />
      </linearGradient>
    </defs>
  </svg>
);

export default function AuthLayout() {
  const { isAuthenticated, user } = useAuthStore();

  if (isAuthenticated && user) {
    const redirectPath = user.role === 'PROFESSOR' ? '/professor' : '/student';
    return <Navigate to={redirectPath} replace />;
  }

  return (
    <div className="min-h-screen bg-void flex">
      {/* Left side - Branding Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Teal-Slate Background */}
        <div className="absolute inset-0 bg-gradient-mesh-strong" />
        <motion.div
          animate={{ 
            y: [-20, 20, -20],
            x: [-10, 10, -10]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 right-1/4 w-[400px] h-[400px] bg-slate/25 rounded-full blur-[100px]"
        />
        <motion.div
          animate={{ 
            y: [20, -20, 20],
            x: [10, -10, 10]
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-1/4 left-1/4 w-[300px] h-[300px] bg-slate-light/20 rounded-full blur-[80px]"
        />
        {/* Subtle coral accent */}
        <div className="absolute bottom-1/3 right-1/4 w-[150px] h-[150px] bg-coral/10 rounded-full blur-[60px]" />

        {/* Content */}
        <div className="relative flex flex-col justify-between p-10 xl:p-14 w-full">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <Logo className="w-10 h-10" />
            <span className="font-semibold text-xl text-white">Ulearning</span>
          </Link>

          {/* Main Content */}
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
                Unlock Your Potential with{' '}
                <span className="text-gradient">Premium Learning</span>
              </h1>
              <p className="mt-5 text-white/50 text-lg leading-relaxed max-w-md">
                Join thousands of learners on our premier platform. 
                Learn from industry experts or share your expertise.
              </p>
            </motion.div>

            {/* Stats - Glass Cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="grid grid-cols-2 gap-3"
            >
              {[
                { value: '10K+', label: 'Active Learners' },
                { value: '500+', label: 'Expert Instructors' },
                { value: '1000+', label: 'Quality Courses' },
                { value: '4.9★', label: 'Average Rating' },
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                  className="card p-4"
                >
                  <div className="text-xl font-bold text-gradient">{stat.value}</div>
                  <div className="text-white/40 text-sm mt-0.5">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Footer */}
          <div className="text-white/30 text-sm">
            © {new Date().getFullYear()} Ulearning. All rights reserved.
          </div>
        </div>
      </div>

      {/* Right side - Auth Form */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-10 lg:px-16 xl:px-20 py-10 relative">
        <div className="absolute inset-0 bg-void" />
        
        {/* Mobile logo */}
        <Link to="/" className="lg:hidden flex items-center gap-3 mb-10 relative">
          <Logo className="w-9 h-9" />
          <span className="font-semibold text-lg text-white">Ulearning</span>
        </Link>

        <div className="w-full max-w-md mx-auto relative">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
